import type { PricePointsResponse } from '@rheora/enrollment-contract';

import { REFERENCE_USAGES, type ReferenceUsage } from '../schemas/shop.schema';
import { formatCentsPerKwh, selectHeadlinePricePoint } from './price.service';

/**
 * Meter-filtered shelf pricing (SHOP-5A) — the effective ¢/kWh a plan costs at
 * the shopper's CHOSEN monthly usage.
 *
 * The §25.475 EFL discloses the average price at three reference usages
 * (500 / 1000 / 2000 kWh) — `PricePointsResponse.kwh500/kwh1000/kwh2000`, each a
 * ¢/kWh string. To price at an arbitrary monthly figure we:
 *   - clamp below 500 → the 500 point, and at/above 2000 → the 2000 point;
 *   - return the exact point when the usage IS a reference level;
 *   - otherwise LINEARLY INTERPOLATE between the two nearest bracketing levels.
 *
 * This is a DISPLAY ESTIMATE for comparison (never a quoted/guaranteed price), so
 * a float interpolation on the ¢/kWh figure is appropriate — unlike dollar money
 * math, which stays exact/BigInt elsewhere. All logic lives here (no `.tsx`).
 */

/** One EFL reference level: its usage and its disclosed ¢/kWh average price. */
export interface EflLevel {
  readonly kwh: number;
  readonly rate: number;
}

/** Parse a disclosed ¢/kWh string to a number; a malformed value reads as 0. */
export function parseRate(value: string): number {
  const parsed = Number.parseFloat(value.trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

/** The three EFL levels for a plan, ascending by usage. */
export function eflLevels(pricePoints: PricePointsResponse): readonly EflLevel[] {
  return [
    { kwh: 500, rate: parseRate(pricePoints.kwh500) },
    { kwh: 1_000, rate: parseRate(pricePoints.kwh1000) },
    { kwh: 2_000, rate: parseRate(pricePoints.kwh2000) },
  ];
}

/**
 * The effective ¢/kWh (a number) for a plan at `monthlyKwh`. Clamped at the ends,
 * exact at a reference level, linearly interpolated between the two nearest.
 */
export function effectiveCentsPerKwhAt(
  pricePoints: PricePointsResponse,
  monthlyKwh: number,
): number {
  const levels = eflLevels(pricePoints);
  const first = levels[0];
  const last = levels[levels.length - 1];
  // Non-null: eflLevels always returns three entries.
  if (first === undefined || last === undefined) return 0;

  if (monthlyKwh <= first.kwh) return first.rate;
  if (monthlyKwh >= last.kwh) return last.rate;

  for (let i = 0; i < levels.length - 1; i += 1) {
    const lower = levels[i];
    const upper = levels[i + 1];
    if (lower === undefined || upper === undefined) continue;
    if (monthlyKwh >= lower.kwh && monthlyKwh <= upper.kwh) {
      if (monthlyKwh === lower.kwh) return lower.rate;
      if (monthlyKwh === upper.kwh) return upper.rate;
      const fraction = (monthlyKwh - lower.kwh) / (upper.kwh - lower.kwh);
      return lower.rate + fraction * (upper.rate - lower.rate);
    }
  }
  return last.rate;
}

/** Shape an effective ¢/kWh number to exactly one decimal place (§25.475 grain). */
export function formatEffectiveRate(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1);
}

/** True when a monthly kWh figure is exactly one of the three EFL reference levels. */
export function isReferenceUsage(monthlyKwh: number): monthlyKwh is ReferenceUsage {
  return REFERENCE_USAGES.some((level) => level === monthlyKwh);
}

/**
 * THE headline ¢/kWh string, for every surface that shows "the price" — shelf card,
 * plan detail, compare row. One function so the shelf and the detail page can never
 * disagree about the same plan at the same usage.
 *
 * ── WHY IT IS NOT JUST `formatEffectiveRate(effectiveCentsPerKwhAt(…))` ────────
 * At an EXACT reference level the number the customer is entitled to see is the one
 * the EFL discloses, BYTE FOR BYTE (PUCT §25.475: the shelf figure must be the
 * disclosed average price). So 500 / 1000 / 2000 return the server's own string via
 * `selectHeadlinePricePoint`, normalised only in SHAPE by `formatCentsPerKwh` (which
 * truncates to one decimal without float math). Any other usage has no disclosed
 * figure to preserve, so it is the interpolated display estimate, rounded to the
 * §25.475 0.1¢ grain by `formatEffectiveRate`.
 *
 * That difference — truncate the disclosed value, round the estimated one — is
 * deliberate and lives ONLY here; callers must not re-derive it.
 *
 * Below 500 and at/above 2000 clamp to the end points (see `effectiveCentsPerKwhAt`).
 * A malformed server rate reads as `"0.0"` on both paths — never `NaN`, never a
 * fabricated price.
 */
export function headlineRateAt(pricePoints: PricePointsResponse, monthlyKwh: number): string {
  if (isReferenceUsage(monthlyKwh)) {
    return formatCentsPerKwh(selectHeadlinePricePoint(pricePoints, monthlyKwh));
  }
  return formatEffectiveRate(effectiveCentsPerKwhAt(pricePoints, monthlyKwh));
}
